import { FormModal, SubmitProps } from '@/components/form-modal.js';
import { FormInputField } from '@/components/forms/form-input-field.js';
import { FormMultiSelect } from '@/components/forms/form-multi-select.js';
import { Button } from '@/components/ui/button.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import {
  buildNameLikeSelectOptions,
  multiSelectOptions,
  multiSelectOptionsHaveNoEmptyValue,
} from '@/components/ui/multi-select-utils.js';
import { lessonTypeaheadSearch } from '@/lib/api-call-hooks.js';
import { createLearningPath, getLearningPaths, updateLearningPath } from '@/lib/api-calls.js';
import { airServerErrorFrom } from '@/lib/error-handling.js';
import { LearningPathWithLessons } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TrashIcon } from 'lucide-react';
import { ReactNode } from 'react';
import { UseFormReturn, useFieldArray } from 'react-hook-form';
import { z } from 'zod';

const formSchema = z.object({
  name: z.string(),
  lessons: multiSelectOptions.array(),
});

interface CreateOrEditLearningPathProps {
  trigger?: ReactNode;
  from?: {
    fromLearningPath: LearningPathWithLessons;
    mode: 'edit' | 'copy';
  };
}

export const CreateOrEditLearningPath = ({ trigger, from }: CreateOrEditLearningPathProps) => {
  const { mutateAsync: createMutation } = useMutation({
    mutationFn: createLearningPath,
  });
  const { mutateAsync: updateMutation } = useMutation({
    mutationFn: updateLearningPath,
  });
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fromLearningPath = from?.fromLearningPath;
  const mode = from?.mode;
  const modalTitle =
    mode === 'edit' ? 'Edit Learning Path' : fromLearningPath ? 'Clone Learning Path' : 'Create Learning Path';
  const defaultLearningPathName: string | undefined = fromLearningPath?.name
    ? fromLearningPath.name + (mode === 'copy' ? ' - copy' : '')
    : undefined;

  const onSubmit = ({ formData, closeModal, reset }: SubmitProps<z.infer<typeof formSchema>>) => {
    const nonEmptyLessons = formData.lessons.filter(multiSelectOptionsHaveNoEmptyValue);
    // at least one lesson
    if (nonEmptyLessons.length === 0) {
      toast({
        title: 'No lessons found',
        description: 'Please add at least one lesson',
      });
      return;
    }

    // no duplicate lessons
    if (nonEmptyLessons.length !== new Set(nonEmptyLessons.map(([{ value: lessonId }]) => lessonId)).size) {
      toast({
        title: 'Duplicate lessons found',
        description: 'Please remove duplicate lessons',
      });
      return;
    }

    const lessons = nonEmptyLessons.map(([{ value: lessonId }], order) => ({ lessonId, order }));

    const apiCall =
      fromLearningPath && mode === 'edit'
        ? () =>
            updateMutation({
              id: fromLearningPath.id,
              data: {
                ...formData,
                lessons,
              },
            })
        : () =>
            createMutation({
              ...formData,
              lessons,
            });

    return apiCall()
      .then(() => {
        toast({
          title: 'Learning path created successfully',
        });
        reset();
        closeModal();
        queryClient.invalidateQueries(getLearningPaths());
      })
      .catch((e) => {
        toast({
          title: airServerErrorFrom(e)?.userFacingMessage || 'Learning path creation failed',
        });
      });
  };

  return (
    <FormModal
      modalTitle={modalTitle}
      formSchema={formSchema}
      onSubmit={onSubmit}
      trigger={trigger || <Button>Create learning path</Button>}
      defaultValues={{
        name: defaultLearningPathName,
        lessons: fromLearningPath
          ? fromLearningPath.learningPathLessons.map(({ lesson }) => [buildNameLikeSelectOptions(lesson)])
          : [],
      }}
    >
      {({ form }) => <CreateLearningPathForm form={form} mode={mode} />}
    </FormModal>
  );
};

const CreateLearningPathForm = ({
  form,
  mode,
}: {
  form: UseFormReturn<z.infer<typeof formSchema>>;
  mode?: 'edit' | 'copy';
}) => {
  const queryClient = useQueryClient();
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'lessons',
  });
  console.log(form.watch('lessons'));

  return (
    <div className="grid gap-4 py-4">
      <FormInputField
        form={form}
        name="name"
        label="Learning path name"
        className="w-full"
        inputProps={{ disabled: mode === 'edit' }}
      />
      <div className="w-full">
        {fields.map((field, index) => {
          return (
            <div key={field.id} className="flex gap-x-2 items-center">
              <FormMultiSelect
                form={form}
                label="Lesson"
                name={`lessons.${index}`}
                multipleSelectorProps={{
                  maxSelected: 1,
                  onSearch: lessonTypeaheadSearch(queryClient),
                  hideClearAllButton: true,
                }}
              />
              <Button
                className="mt-2"
                variant="secondary"
                onClick={() => {
                  remove(index);
                }}
                icon={TrashIcon}
              />
            </div>
          );
        })}
      </div>
      <div>
        <Button onClick={() => append([[]])} type="button">
          Add lesson
        </Button>
      </div>
    </div>
  );
};
